import api from 'api';
import { PRODUCT_TO_NAME } from 'lib/enums/Product';
import { OrderModel } from 'lib/model/objects/orderModel';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { EUser, ESnapshotExists } from 'lib/types';
import { logAndCaptureException } from 'utils';
import { ColumnService } from 'lib/services/directory';
import {
  finalizeOrderInvoice,
  markOrderInvoiceAsPaidDirectToPublisher
} from './paymentProcessing';
import { SuccessModalType } from '../PlacementFlowStepSelector';

export type CompleteOrderResult = {
  redirectUrl?: string;
  showModal?: SuccessModalType;
};

export const completeOrder = async ({
  orderModel,
  version,
  consolidatedOrderPricing,
  newInvoiceId,
  refundRequestedInCents,
  invoiceMarkedAsPaid,
  paymentNotes,
  submitWithoutPaying,
  user,
  orderIsIOC
}: {
  orderModel: OrderModel;
  version: number;
  consolidatedOrderPricing: { totalInCents: number } | undefined;
  newInvoiceId: string | null;
  refundRequestedInCents: number;
  invoiceMarkedAsPaid: boolean;
  paymentNotes: string;
  submitWithoutPaying: boolean;
  user: ESnapshotExists<EUser> | null | undefined;
  orderIsIOC: boolean;
}): Promise<ResponseOrError<CompleteOrderResult>> => {
  const productSingularLower =
    PRODUCT_TO_NAME[orderModel.modelData.product].singular.toLowerCase();

  // TODO: Get Stripe threshold for 'free' which will be automatically paid
  // Only use submit-free when a new invoice exists for this version; a free
  // edit without a new invoice (price unchanged) routes through confirm-edits.
  if (consolidatedOrderPricing?.totalInCents === 0 && newInvoiceId) {
    const { error } =
      await api.safePostWithParams<'orders/:orderId/submit-free'>(
        `orders/${orderModel.id}/submit-free`,
        { version }
      );
    if (error) {
      return wrapError(new Error(error));
    }
    return wrapSuccess({
      showModal: SuccessModalType.FreeOrderSubmission
    });
  }

  // Confirm-edits: same invoice is reused (no new invoice, or partial refund)
  if (!newInvoiceId || refundRequestedInCents) {
    const { error } =
      await api.safePostWithParams<'orders/:orderId/confirm-edits'>(
        `orders/${orderModel.id}/confirm-edits`,
        { version, refundRequestedInCents }
      );
    if (error) {
      return wrapError(new Error(error));
    }
    // Return early when invoice does not need to be marked as paid
    if (refundRequestedInCents > 0 || (!orderIsIOC && !invoiceMarkedAsPaid)) {
      return wrapSuccess({
        outcome: 'edits-confirmed',
        showModal: SuccessModalType.EditConfirmation
      });
    }
  }

  // Resolve the invoice ID for mark-as-paid / finalize.
  // Only reached when a new invoice exists, or when !newInvoiceId with IOC/mark-as-paid.
  let invoiceId: string;
  if (newInvoiceId) {
    invoiceId = newInvoiceId;
  } else {
    const [invoiceError, invoice] = await orderModel.getInvoice(version);
    if (invoiceError || !invoice) {
      const err = invoiceError || new Error('Invoice not found');
      logAndCaptureException(
        ColumnService.PAYMENTS,
        err,
        `Failed to retrieve invoice for order ${orderModel.id} version ${version} during order completion`
      );
      return wrapError(err);
    }
    // Already marked as paid
    if (!invoice.isUnpaid()) {
      return wrapSuccess({
        outcome: 'edits-confirmed',
        showModal: SuccessModalType.EditConfirmation
      });
    }
    invoiceId = invoice.id;
  }

  if (orderIsIOC || invoiceMarkedAsPaid) {
    const [paidError] = await markOrderInvoiceAsPaidDirectToPublisher({
      orderModel,
      invoiceId,
      isIOC: orderIsIOC,
      paymentNotes,
      user
    });
    if (paidError) {
      return wrapError(paidError);
    }
    return wrapSuccess({
      showModal: orderIsIOC
        ? SuccessModalType.IOCOrderSubmission
        : SuccessModalType.MarkInvoicePaidConfirmation
    });
  }

  const [finalizeError] = await finalizeOrderInvoice(invoiceId);
  if (finalizeError) {
    return wrapError(finalizeError);
  }

  if (submitWithoutPaying) {
    const { error: submissionError } =
      await api.safePostWithParams<'orders/:orderId/submit-unpaid'>(
        `orders/${orderModel.id}/submit-unpaid`,
        { version }
      );
    if (submissionError) {
      return wrapError(new Error(submissionError));
    }
    return wrapSuccess({
      redirectUrl: `${window.location.origin}/${productSingularLower}/${orderModel.id}`
    });
  }

  return wrapSuccess({
    redirectUrl: `${window.location.origin}/invoices/${invoiceId}/pay`
  });
};
